import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { KpiTileSpec, KpiTilesSpec, KpiTone } from '../../models/chat.models';

type TrendSign = 'up' | 'down' | 'flat';

@Component({
  selector: 'app-chat-kpi-tiles',
  templateUrl: './chat-kpi-tiles.component.html',
  styleUrls: ['./chat-kpi-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatKpiTilesComponent implements OnChanges {
  /** Raw JSON payload coming from the fenced ```kpi_tiles``` block. */
  @Input({ required: true }) spec!: string;

  spec_: KpiTilesSpec | null = null;
  errorRaw: string | null = null;

  ngOnChanges(): void {
    this.spec_ = null;
    this.errorRaw = null;

    try {
      const parsed = JSON.parse(this.spec) as KpiTilesSpec;
      if (
        !parsed ||
        !Array.isArray(parsed.tiles) ||
        parsed.tiles.length === 0 ||
        !parsed.tiles.every((t) => this.isValidTile(t))
      ) {
        this.errorRaw = this.spec;
        return;
      }
      this.spec_ = {
        title: typeof parsed.title === 'string' ? parsed.title : undefined,
        tiles: parsed.tiles.map((t) => ({
          label: t.label,
          value: t.value,
          tone: this.normalizeTone(t.tone),
          hint: typeof t.hint === 'string' ? t.hint : undefined,
          trend: typeof t.trend === 'string' ? t.trend : undefined,
        })),
      };
    } catch {
      this.errorRaw = this.spec;
    }
  }

  /** CSS class on the grid container — drives `grid-template-columns` via SCSS. */
  get gridClass(): string {
    const n = this.spec_?.tiles.length ?? 0;
    if (n <= 1) return 'tiles-1';
    if (n === 2) return 'tiles-2';
    if (n === 3) return 'tiles-3';
    if (n === 4) return 'tiles-4';
    return 'tiles-3plus';
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Maps the tone to the value color used inline (kept here so the SCSS stays simple). */
  valueColor(tile: KpiTileSpec): string {
    switch (tile.tone) {
      case 'good':
        return '#00C48C';
      case 'warning':
        return '#FBBF24';
      case 'bad':
        return '#F87171';
      case 'neutral':
        return '#FFFFFF';
      default:
        return '#00C48C';
    }
  }

  /** Trend color: + → green, - → red, otherwise neutral grey. */
  trendColor(trend: string | undefined): string {
    const sign = this.trendSign(trend);
    if (sign === 'up') return '#00C48C';
    if (sign === 'down') return '#F87171';
    return '#9CA3AF';
  }

  private trendSign(trend: string | undefined): TrendSign {
    if (!trend) return 'flat';
    const t = trend.trim();
    if (t.startsWith('+')) return 'up';
    if (t.startsWith('-') || t.startsWith('−')) return 'down';
    return 'flat';
  }

  private isValidTile(t: KpiTileSpec | undefined): t is KpiTileSpec {
    return !!t && typeof t.label === 'string' && typeof t.value === 'string';
  }

  private normalizeTone(tone: KpiTone | undefined): KpiTone {
    if (tone === 'good' || tone === 'warning' || tone === 'bad' || tone === 'neutral') {
      return tone;
    }
    return 'good';
  }
}
